import type { WidgetFieldSource } from "@/domains/buildings/types/buildingWidget";
import type { AttributeHistoryEntry } from "@/hooks/buildings/useAttributeHistory";
import { groupNonUserSources, type SourceGroup } from "./sourceGroups";
import { visibleTooltipSources } from "./visibleTooltipSources";

export type TimelineNodeKind = WidgetFieldSource["kind"];

/**
 * One user edit in the value-history timeline (WHA-3720). The timeline shows only
 * user changes; machine sources surface as per-node "supporting sources" and in
 * the modal's "Franklin findings" section.
 */
export interface UserHistoryNode {
  key: string;
  /** Raw (canonical) value — localized at render time. */
  value: string;
  timestamp: string;
  editorName?: string | null;
  editorId?: number | null;
  editorAvatarUrl?: string | null;
  editorColor?: string | null;
}

/**
 * The full append-only user-edit history for one attribute, newest-first.
 * Entries with an empty timestamp sort last (a missing timestamp can't be
 * ordered against real ones).
 */
export function buildUserHistoryNodes(
  history: AttributeHistoryEntry[] | undefined
): UserHistoryNode[] {
  return (history ?? [])
    .map((h, i) => ({
      key: `user-${i}-${h.createdAt}`,
      value: h.attributeValue ?? "",
      timestamp: h.createdAt,
      editorName: h.editedByName,
      editorId: h.editedBy,
      editorAvatarUrl: h.editedByAvatarUrl,
      editorColor: h.editedByColor,
    }))
    .sort((a, b) => {
      if (!a.timestamp && !b.timestamp) return 0;
      if (!a.timestamp) return 1;
      if (!b.timestamp) return -1;
      return a.timestamp > b.timestamp ? -1 : a.timestamp < b.timestamp ? 1 : 0;
    });
}

/**
 * Machine sources (EHR / extractor / IFC) whose value backs `value`, grouped per
 * (kind, value). Reuses the sidebar tooltip's canonical value-matcher
 * (`visibleTooltipSources`) so the "supporting sources" shown here match what the
 * field row shows — including comma-joined multi-select segment matching. User
 * entries are excluded by `groupNonUserSources`.
 */
export function supportingGroupsForValue(
  sources: WidgetFieldSource[],
  value: string,
  isMultiSelect: boolean
): SourceGroup[] {
  return groupNonUserSources(visibleTooltipSources(sources, value, isMultiSelect));
}
